import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';

const OTP_EXPIRY_TIME = 300; // 5 minutes (in seconds)

const LoginDialog = ({ onClose, onLogin }) => {
  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
  const [otpInterval, setOtpInterval] = useState(null);
  const [isProcessingPhone, setIsProcessingPhone] = useState(false);
  const [isProcessingVerification, setIsProcessingVerification] =
    useState(false);
  const [isProcessingResendOtp, setIsProcessingResendOtp] = useState(false);

  useEffect(() => {
    return () => {
      if (otpInterval) clearInterval(otpInterval);
    };
  }, [otpInterval]);

  const startOtpTimer = () => {
    if (otpInterval) clearInterval(otpInterval);
    setOtpTimer(OTP_EXPIRY_TIME);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev === 1) clearInterval(interval);
        return prev - 1;
      });
    }, 1000);
    setOtpInterval(interval);
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessingPhone(true);
    try {
      await api.post('/auth/customer/login', { phoneNumber });
      setStep('verify');
      startOtpTimer();
    } catch (error) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsProcessingPhone(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessingVerification(true);
    try {
      const response = await api.post('/auth/customer/verify-otp', {
        phoneNumber,
        verificationCode,
      });
      if (response.data.token) {
        localStorage.setItem('customerToken', response.data.token);
        // api.defaults.headers.common[
        //   'Authorization'
        // ] = `Bearer ${response.data.token}`;

        onLogin(response.data.token);
        onClose();
      }
    } catch (error) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsProcessingVerification(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setIsProcessingResendOtp(true);
    try {
      await api.post('/auth/customer/login', { phoneNumber });
      startOtpTimer();
    } catch (error) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsProcessingResendOtp(false);
    }
  };

  const handleGoBack = () => {
    setStep('phone');
    setVerificationCode('');
    clearInterval(otpInterval);
    setOtpTimer(OTP_EXPIRY_TIME);
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white p-6 rounded-lg w-96'>
        <h2 className='text-xl font-bold mb-4'>Login / Signup Required</h2>
        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className='space-y-4'>
            <div>
              <label
                htmlFor='phoneNumber'
                className='block text-sm font-medium text-gray-700'
              >
                Phone Number
              </label>
              <input
                type='tel'
                id='phoneNumber'
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
                placeholder='Enter your phone number'
              />
            </div>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              type='submit'
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              disabled={isProcessingPhone}
            >
              {isProcessingPhone ? 'Processing...' : 'Send Verification Code'}
            </button>
          </form>
        )}
        {step === 'verify' && (
          <form onSubmit={handleVerificationSubmit} className='space-y-4'>
            <div>
              <label
                htmlFor='verificationCode'
                className='block text-sm font-medium text-gray-700'
              >
                Verification Code
              </label>
              <input
                type='text'
                id='verificationCode'
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
                placeholder='Enter verification code'
              />
            </div>
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            <button
              type='submit'
              className='w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              disabled={isProcessingVerification}
            >
              {isProcessingVerification ? 'Processing...' : 'Verify and Login'}
            </button>
            <button
              onClick={handleGoBack}
              className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400'
            >
              Go Back
            </button>
            <button
              onClick={handleResendOtp}
              className='w-full mt-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400'
              disabled={isProcessingResendOtp}
            >
              {isProcessingResendOtp ? 'Processing...' : 'Resend OTP'}
            </button>
            <p>OTP expires in {otpTimer} seconds</p>
          </form>
        )}
        <button
          onClick={onClose}
          className='mt-4 w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded'
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default LoginDialog;

// // components/LoginDialog.js
// import React from 'react';
// import { useNavigate } from 'react-router-dom';

// const LoginDialog = ({ onClose, message }) => {
//   const navigate = useNavigate();

//   return (
//     <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
//       <div className='bg-white p-6 rounded-lg w-96'>
//         <h2 className='text-xl font-bold mb-4'>Login Required</h2>
//         <p className='mb-4'>
//           {message || 'Please log in or sign up to access this feature.'}
//         </p>
//         <div className='flex justify-between'>
//           <button
//             onClick={() => navigate('/login')}
//             className='bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded'
//           >
//             Log In
//           </button>
//           <button
//             onClick={() => navigate('/signup')}
//             className='bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded'
//           >
//             Sign Up
//           </button>
//           <button
//             onClick={onClose}
//             className='bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded'
//           >
//             Cancel
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default LoginDialog;
